import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plane, Sparkles, Languages, ShieldAlert, Luggage, FileUser, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { localeUpperCase } from '../../lib/textCase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CABIN_COURSE_ID = 'course_cabin_main_v1';

// Bu 5 modül, Kabin Memurluğu kursunun (course_cabin_main_v1) GERÇEK modül
// grubu — /api/courses/{id}/modules canlı olarak çekilir, başlıklar buradan
// gelir. Madde işaretleri modülün gerçek konusuna göre yazılmış kısa
// açıklamalardır (uydurma özellik iddiası içermez).
const MODULE_META = [
  { icon: Plane, bulletKeys: ['landing2.curriculum.mod1.bullet1', 'landing2.curriculum.mod1.bullet2'] },
  { icon: Sparkles, bulletKeys: ['landing2.curriculum.mod2.bullet1', 'landing2.curriculum.mod2.bullet2'] },
  { icon: Luggage, bulletKeys: ['landing2.curriculum.mod3.bullet1', 'landing2.curriculum.mod3.bullet2'] },
  { icon: Languages, bulletKeys: ['landing2.curriculum.mod4.bullet1', 'landing2.curriculum.mod4.bullet2'] },
  { icon: ShieldAlert, bulletKeys: ['landing2.curriculum.mod5.bullet1', 'landing2.curriculum.mod5.bullet2'] },
];

// FALLBACK — API cagrisi basarisiz olur ya da modul listesi bos donerse
// (bkz. asagidaki catch/then), sayfa bos kalmasin diye mockup'taki mufredat
// deseniyle doldurulur. Bu, gercek API verisinin YOKLUGUNDA gosterilen bir
// yedek icerik — DB'ye yeni bir sey yazilmiyor, sadece frontend sunumu.
const FALLBACK_MODULES = [
  { icon: Plane, titleKey: 'landing2.curriculum.fallback.mod1.title', bulletKeys: ['landing2.curriculum.fallback.mod1.bullet1', 'landing2.curriculum.fallback.mod1.bullet2', 'landing2.curriculum.fallback.mod1.bullet3', 'landing2.curriculum.fallback.mod1.bullet4'] },
  { icon: Sparkles, titleKey: 'landing2.curriculum.fallback.mod2.title', bulletKeys: ['landing2.curriculum.fallback.mod2.bullet1', 'landing2.curriculum.fallback.mod2.bullet2', 'landing2.curriculum.fallback.mod2.bullet3', 'landing2.curriculum.fallback.mod2.bullet4'] },
  { icon: Languages, titleKey: 'landing2.curriculum.fallback.mod3.title', bulletKeys: ['landing2.curriculum.fallback.mod3.bullet1', 'landing2.curriculum.fallback.mod3.bullet2', 'landing2.curriculum.fallback.mod3.bullet3', 'landing2.curriculum.fallback.mod3.bullet4'] },
  { icon: ShieldAlert, titleKey: 'landing2.curriculum.fallback.mod4.title', bulletKeys: ['landing2.curriculum.fallback.mod4.bullet1', 'landing2.curriculum.fallback.mod4.bullet2', 'landing2.curriculum.fallback.mod4.bullet3', 'landing2.curriculum.fallback.mod4.bullet4'] },
  { icon: Luggage, titleKey: 'landing2.curriculum.fallback.mod5.title', bulletKeys: ['landing2.curriculum.fallback.mod5.bullet1', 'landing2.curriculum.fallback.mod5.bullet2', 'landing2.curriculum.fallback.mod5.bullet3', 'landing2.curriculum.fallback.mod5.bullet4'] },
];

export default function CurriculumGrid() {
  const { t, lang, getField } = useLanguage();
  const [modules, setModules] = useState(null); // null = loading, [] = error/empty

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/courses/${CABIN_COURSE_ID}/modules`)
      .then((res) => {
        if (cancelled) return;
        setModules((res.data?.modules || []).slice(0, 5));
      })
      .catch(() => { if (!cancelled) setModules([]); });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="bg-[#F7F5F0] text-[#0E1F3D] py-16 md:py-20 px-6" data-testid="landing2-curriculum">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-xl mb-10">
          <div className="text-xs font-bold tracking-widest text-[#7A5D10]">
            {localeUpperCase(t('landing2.curriculum.eyebrow'), lang)}
          </div>
          <h2 className="landing2-display text-3xl md:text-[40px] mt-3">
            {localeUpperCase(t('landing2.curriculum.title'), lang)}
          </h2>
          <p className="text-[17px] text-[#5a6b60] mt-4 leading-relaxed">
            {t('landing2.curriculum.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {modules === null && [0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-56 rounded-2xl bg-white border border-[#e6e2d8] animate-pulse" />
          ))}

          {modules !== null && modules.length > 0 && modules.map((m, i) => {
            const meta = MODULE_META[i] || MODULE_META[0];
            const Icon = meta.icon;
            return (
              <div key={m.module_id || i} className="bg-white border border-[#e6e2d8] rounded-2xl p-[26px]" data-testid="curriculum-card">
                <div className="w-11 h-11 rounded-[11px] bg-[#0E1F3D] text-[#C9A227] flex items-center justify-center">
                  <Icon size={20} aria-hidden="true" />
                </div>
                <h3 className="font-extrabold text-[19px] mt-4 mb-3.5">{getField(m, 'title')}</h3>
                <div className="flex flex-col gap-2.5">
                  {meta.bulletKeys.map((k) => (
                    <div key={k} className="flex gap-2.5 text-sm leading-snug text-[#46564c]">
                      <Check size={16} className="text-[#16794a] flex-shrink-0 mt-0.5" strokeWidth={3} aria-hidden="true" />
                      {t(k)}
                    </div>
                  ))}
                  <div className="flex gap-2.5 text-sm leading-snug text-[#46564c]">
                    <Check size={16} className="text-[#16794a] flex-shrink-0 mt-0.5" strokeWidth={3} aria-hidden="true" />
                    {(m.lessons || []).length} {t('landing2.common.lessonsSuffix')}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Fallback — API modul verisi bos/hatali donduyse sayfa bos kalmasin diye
              mockup'taki mufredat desenine dusulur (gercek kurs kaydi/DB degisikligi yok). */}
          {modules !== null && modules.length === 0 && FALLBACK_MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.titleKey} className="bg-white border border-[#e6e2d8] rounded-2xl p-[26px]" data-testid="curriculum-card-fallback">
                <div className="w-11 h-11 rounded-[11px] bg-[#0E1F3D] text-[#C9A227] flex items-center justify-center">
                  <Icon size={20} aria-hidden="true" />
                </div>
                <h3 className="font-extrabold text-[19px] mt-4 mb-3.5">{t(m.titleKey)}</h3>
                <div className="flex flex-col gap-2.5">
                  {m.bulletKeys.map((k) => (
                    <div key={k} className="flex gap-2.5 text-sm leading-snug text-[#46564c]">
                      <Check size={16} className="text-[#16794a] flex-shrink-0 mt-0.5" strokeWidth={3} aria-hidden="true" />
                      {t(k)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* 6. kart: CV Sihirbazı + Mülakat Simülasyonu — gerçek özellikler, ayrı kurs kaydı değil */}
          <Link
            to="/programs/kabin-memurlugu/cv"
            className="bg-white border border-[#e6e2d8] rounded-2xl p-[26px] hover:-translate-y-1 transition-transform"
            data-testid="curriculum-card-cv"
          >
            <div className="w-11 h-11 rounded-[11px] bg-[#0E1F3D] text-[#C9A227] flex items-center justify-center">
              <FileUser size={20} aria-hidden="true" />
            </div>
            <h3 className="font-extrabold text-[19px] mt-4 mb-3.5">{t('landing2.curriculum.card6.title')}</h3>
            <div className="flex flex-col gap-2.5">
              {['landing2.curriculum.card6.bullet1', 'landing2.curriculum.card6.bullet2', 'landing2.curriculum.card6.bullet3'].map((k) => (
                <div key={k} className="flex gap-2.5 text-sm leading-snug text-[#46564c]">
                  <Check size={16} className="text-[#16794a] flex-shrink-0 mt-0.5" strokeWidth={3} aria-hidden="true" />
                  {t(k)}
                </div>
              ))}
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
